// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { xUpdateOrderStatus } from "@electrowire/App/Handlers/XNG/OrderHistoryStatus";
import requestSystemList from "@gcg/App/Handlers/Data/RequestSystemListHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { Icon } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    showReorderProducts = "showReorderProducts",
    title = "title",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.showReorderProducts]: boolean;
        [fields.title]: string;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        OrderDetailsPage: getPageLinkByPageType(state, "OrderDetailsPage"),
        language: state.context.session.language,
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    loadOrders,
    updateSearchFields,
    reorder,
    requestSystemList,
    xUpdateOrderStatus,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    HasHistory &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

// changing this to match the key of orders in the store
const recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    pageSize: 5,
    sort: "OrderDate DESC",
    status: [],
};

export interface ClosedOrdersStyles {
    errorText?: TypographyPresentationProps;
    noOrdersFoundText?: TypographyPresentationProps;
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    viewAllLink?: InjectableCss;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    trackingNumber?: TypographyPresentationProps;
}

export const closedOrdersStyles: ClosedOrdersStyles = {};

const styles = closedOrdersStyles;

const defaultCustomCss = ``;

const ClosedOrders = (props: Props) => {
    const { ordersDataView, orderHistoryPageLink, fields, requestSystemList, xUpdateOrderStatus } = props;
    const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;
    const orders = ordersDataView.value;
    const enableCommerceExperience = props.customSettings.enableCommerceExperience.toLowerCase() === "true";
    const [closeOrderStatus, setCloseOrderStatus] = useState<string[]>([]);
    
    const customCssWrapper = {
        css: css`
            margin-bottom: 20px;
            ${props.fields.customCSS}
        `,
    };
    
    useEffect(() => {
        requestSystemList({
            slug: "Custom_CompletedOrderStatus",
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    const result = resultProps.apiResult?.result;
                    const closedStatuses = result.map((status) => status.name);
                    setCloseOrderStatus(closedStatuses);
                }
            },
        });
    }, []);

    useEffect(() => {
        if(!closeOrderStatus.length){return;}
        recentOrdersParameter.pageSize = props.fields.numberOfRecords;
        recentOrdersParameter.status = closeOrderStatus;

        if (props.canViewOrders && !props.ordersDataView.value) {
            props.updateSearchFields(recentOrdersParameter);
            props.loadOrders(recentOrdersParameter);
        }
    },[closeOrderStatus]);

    const headerClick = (sortField: string) => {
        const sort = props.parameter.sort === sortField ? `${sortField} DESC` : sortField;

        recentOrdersParameter.sort = sort;

        props.updateSearchFields({ sort });
        props.loadOrders(recentOrdersParameter);
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (props.parameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.parameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }

        return sorted as SortOrderOptions;
    };

    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string) => {
        if (!props.showAddToCartConfirmationDialog) {
            return;
        }

        props.toaster.addToast({
            children: (
                <>
                    <OrderDetailPageTypeLink title={orderNumber} orderNumber={linkOrderNumber} />
                    &nbsp;{translate("added to cart")}
                </>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    if (!props.canViewOrders) {
        return null;
    }

    if (!orders || !closeOrderStatus.length) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!orders.length || !props.canViewOrders) {
        return null;
    }

    const rows = orders.map(order => {
        return {
            id: order.id,
            linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
            date: order.orderDate
                ? getLocalizedDateTime({
                      dateTime: new Date(order.orderDate),
                      language: props.language,
                  })
                : "",
            orderNumber: order.erpOrderNumber ? order.erpOrderNumber : translate("Pending"),
            shipTo: `${order.stCompanyName} ${order.btAddress1} ${order.btAddress2} ${order.shipToCity} ${order.shipToState}`,
            trackingNumber: order.properties.trackingNumber,
            po: order.customerPO,
            total: order.orderGrandTotalDisplay,
            reorderOrderNumber: order.erpOrderNumber || order.webOrderNumber,
            webOrderNumber: order.webOrderNumber,
        };
    });

    const handleDataTableRowClick = (webOrderNumber: any) => {
        if (!webOrderNumber) {
            return;
        }
        props.OrderDetailsPage && props.history.push(`${props.OrderDetailsPage.url}?orderNumber=${webOrderNumber}`);
    };

    const viewClosedOrders = () => {
        props.history.push(`${orderHistoryUrl}`);
        xUpdateOrderStatus({ updateOrderStatus: closeOrderStatus });
    };

    const showTrackingNumbers = (trackingNumber: any) => {
        const trackingNumbersArray = trackingNumber ? trackingNumber.split(",") : [];

        return (
            <StyledWrapper>
                {trackingNumbersArray.length === 1 ? (
                    <Typography {...styles.trackingNumber}>{trackingNumbersArray[0]}</Typography>
                ) : (
                    trackingNumbersArray.map((num: string, index: React.Key | undefined) => (
                        <Typography {...styles.trackingNumber} key={index}>
                            {num.trim()}
                        </Typography>
                    ))
                )}
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.headerContainer}>
                <Typography variant="h2">{fields.title}</Typography>
                <StyledWrapper {...styles.viewAllLink}>
                    <Link onClick={viewClosedOrders}>{translate("View All")}</Link>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader
                            {...styles.orderNumberHeader}
                            title={translate("Order Number")}
                            sorted={sorted("webOrderNumber")}
                            onSortClick={() => headerClick("webOrderNumber")}
                        >
                            {translate("Order #", "webOrderNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderDateHeader}
                            sorted={sorted("orderDate")}
                            onSortClick={() => headerClick("orderDate")}
                        >
                            {translate("Order Date", "orderDate")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.customerPOHeader}
                            title={translate("Purchase Order Number")}
                            sorted={sorted("customerPO")}
                            onSortClick={() => headerClick("customerPO")}
                        >
                            {translate("PO/Ref #", "customerPO")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.shipToHeader}
                            sorted={sorted("stCompanyName")}
                            onSortClick={() => headerClick("stCompanyName")}
                        >
                            {translate("Ship To / Pick Up", "stCompanyName")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.statusHeader}>
                            {translate("tracking #", "trackingNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderTotalHeader}
                            sorted={sorted("orderTotal")}
                            onSortClick={() => headerClick("orderTotal")}
                        >
                            {translate("Order Total", "orderTotal")}
                        </DataTableHeader>

                        {props.fields.showReorderProducts && (
                            <DataTableHeader {...styles.reorderHeader} title={translate("reorder")}>
                                {translate("Add to Cart", "add to Cart")}
                            </DataTableHeader>
                        )}
                    </DataTableHead>
                    <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                        {rows?.map(
                            ({
                                id,
                                linkOrderNumber,
                                date,
                                orderNumber,
                                shipTo,
                                trackingNumber,
                                po,
                                total,
                                reorderOrderNumber,
                                webOrderNumber,
                            }) => {
                                return (
                                    <DataTableRow
                                        key={id}
                                        onClick={() => {
                                            handleDataTableRowClick(webOrderNumber);
                                        }}
                                    >
                                        <DataTableCell
                                            {...styles.orderNumberCells}
                                            data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                        >
                                            {orderNumber}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.orderDateCells}
                                            data-test-selector="orderHistoryTable_tableCell_date"
                                        >
                                            {date}
                                        </DataTableCell>
                                        <DataTableCell {...styles.shipToCells}>{po}</DataTableCell>
                                        <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>

                                        <DataTableCell  data-test-selector="orderHistoryTable_tableCell_status">
                                            {showTrackingNumbers(trackingNumber)}
                                        </DataTableCell>
                                        <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>
                                        {props.fields.showReorderProducts && (
                                            <DataTableCell {...styles.reorderCells}>
                                                {props.isReordering[reorderOrderNumber] && (
                                                    <LoadingSpinner {...styles.reorderButtonSpinner} />
                                                )}
                                                {!props.isReordering[reorderOrderNumber] && enableCommerceExperience && (
                                                    <Button
                                                        disabled={Object.keys(props.isReordering).length > 0}
                                                        {...styles.reorderButton}
                                                        onClick={event => {
                                                            // Stop the event propagation from clicking parent event which is clicking the row
                                                            event.stopPropagation();
                                                            props.reorder({
                                                                orderNumber: reorderOrderNumber,
                                                                onSuccess: () =>
                                                                    onReorderSuccess(
                                                                        reorderOrderNumber,
                                                                        linkOrderNumber,
                                                                    ),
                                                            });
                                                        }}
                                                    >
                                                        <Icon src={ShoppingCart} size={22} />
                                                    </Button>
                                                )}
                                            </DataTableCell>
                                        )}
                                    </DataTableRow>
                                );
                            },
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(ClosedOrders))),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: translate("Order History - fully Shipped Orders"),
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.showReorderProducts,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
